import { useEffect, useRef, useState } from "react";
import { Ear, Loader2, Mic, Square, Star, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { SpeakButton } from "./SpeakButton";
import { kidsApi, formatApiError } from "../lib/api";

const useRecorder = (onClip) => {
  const [busy, setBusy] = useState("idle");
  const rec = useRef(null);
  const cancelled = useRef(false);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const type = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const mr = new MediaRecorder(stream, { mimeType: type });
      const parts = [];
      mr.ondataavailable = (e) => e.data.size && parts.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (cancelled.current) { setBusy("idle"); return; }
        setBusy("thinking");
        try {
          await onClip(new Blob(parts, { type }), type.includes("webm") ? "webm" : "mp4");
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail));
        }
        setBusy("idle");
      };
      cancelled.current = false;
      mr.start();
      rec.current = mr;
      setBusy("listening");
      setTimeout(() => mr.state === "recording" && mr.stop(), 30000);
    } catch {
      toast.error("I can't use the microphone. Ask a grown-up to allow it.");
    }
  };
  const stop = () => rec.current?.state === "recording" && rec.current.stop();
  const cancel = () => { cancelled.current = true; stop(); };
  return { busy, start, stop, cancel };
};

const MicButton = ({ r, prefix, size = 30 }) =>
  r.busy === "listening" ? (
    <button data-testid={`${prefix}-stop-btn`} onClick={r.stop} className="what-mic is-on"><Square size={size} /></button>
  ) : (
    <button data-testid={`${prefix}-start-btn`} onClick={r.start} disabled={r.busy === "thinking"} className="what-mic">
      {r.busy === "thinking" ? <Loader2 size={size} className="animate-spin" /> : <Mic size={size + 2} />}
    </button>
  );

const StarPractice = ({ r, onStars, registerCancel }) => {
  const [words, setWords] = useState(r.star_words.map((w) => ({ word: w, ok: false, starred: false })));
  const [heard, setHeard] = useState("");
  const rec = useRecorder(async (blob, ext) => {
    const out = await kidsApi.practice(blob, ext, r.listen_id);
    setWords(out.results);
    setHeard(out.said);
    onStars(out.total_stars);
    if (out.new_stars) toast.success(`You got ${out.new_stars} star${out.new_stars > 1 ? "s" : ""}!`);
    else toast("Nearly! Listen and try once more.");
  });
  useEffect(() => registerCancel(rec.cancel));
  const done = words.every((w) => w.starred);
  return (
    <div data-testid="star-practice" className="rounded-2xl border border-[#FBBF24]/30 bg-[#FBBF24]/[0.05] p-4">
      <p className="text-[11px] font-mono uppercase tracking-[0.16em] text-[#FBBF24] flex items-center gap-1.5"><Star size={12} /> Pronunciation Stars</p>
      <p className="text-sm text-slate-300 mt-1">{done ? "You said every word right. Super star!" : "Say the new words out loud to win a star for each one."}</p>
      <div className="flex flex-wrap gap-2 mt-3">
        {words.map((w, i) => (
          <span key={w.word} data-testid={`star-word-${i}`} data-starred={w.starred} className={`what-star-chip ${w.starred ? "is-won" : ""}`}>
            <Star size={13} fill={w.starred ? "currentColor" : "none"} /> {w.word}
            <SpeakButton load={() => kidsApi.speak(w.word)} testid={`star-word-listen-${i}`} />
          </span>
        ))}
      </div>
      {!done && (
        <div className="flex items-center gap-4 mt-4">
          <MicButton r={rec} prefix="star-practice" size={20} />
          <p data-testid="star-practice-state" className="text-sm text-slate-400">
            {rec.busy === "listening" ? "Say the words… then press stop" : rec.busy === "thinking" ? "Checking…" : "Press and say the words"}
          </p>
        </div>
      )}
      {heard && <p data-testid="star-practice-heard" className="text-xs text-slate-500 mt-3">I heard: "{heard}"</p>}
    </div>
  );
};

const Result = ({ r, onStars, registerCancel }) => (
  <div data-testid="listen-result" className="space-y-4 mt-2">
    <div>
      <p className="text-[11px] font-mono uppercase tracking-[0.16em] text-slate-500">You said</p>
      <p data-testid="listen-said" className="text-slate-200 mt-1">"{r.said || "…"}"</p>
    </div>
    {r.corrections.length > 0 && (
      <div>
        <p className="text-[11px] font-mono uppercase tracking-[0.16em] text-[#FBBF24]">Better words</p>
        <ul data-testid="listen-corrections" className="mt-2 space-y-2">
          {r.corrections.map((c, i) => (
            <li key={i} data-testid={`listen-correction-${i}`} className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-3 text-sm">
              <span className="line-through text-red-300">{c.wrong}</span> → <span className="font-semibold text-[#4ADE80]">{c.better}</span>
              {c.why && <p className="text-slate-400 mt-1">{c.why}</p>}
            </li>
          ))}
        </ul>
      </div>
    )}
    {r.corrected && r.corrections.length > 0 && <p data-testid="listen-corrected" className="text-sm">Try saying: <span className="text-white font-semibold">"{r.corrected}"</span></p>}
    {r.listen_id && r.star_words?.length > 0 && <StarPractice key={r.listen_id} r={r} onStars={onStars} registerCancel={registerCancel} />}
    {r.praise && <p data-testid="listen-praise" className="text-[#38BDF8] font-semibold">{r.praise}</p>}
    {r.reply && (
      <div className="flex items-start justify-between gap-3 rounded-xl bg-[#38BDF8]/5 border border-[#38BDF8]/25 p-3">
        <p data-testid="listen-reply" className="text-slate-100">{r.reply}</p>
        <SpeakButton big load={() => kidsApi.speak(`${r.praise} ${r.corrected && r.corrections.length ? `Try saying: ${r.corrected}.` : ""} ${r.reply}`)} testid="listen-reply-listen" />
      </div>
    )}
  </div>
);

export const ListenMode = ({ open, onOpenChange }) => {
  const [result, setResult] = useState(null);
  const [stars, setStars] = useState(null);
  const practiceCancel = useRef(null);
  const rec = useRecorder(async (blob, ext) => {
    const out = await kidsApi.listen(blob, ext);
    setResult(out);
    setStars(out.total_stars);
  });
  useEffect(() => {
    if (open) kidsApi.stars().then((d) => setStars(d.total)).catch(() => {});
  }, [open]);
  const close = (v) => {
    if (!v) { rec.cancel(); practiceCancel.current?.(); }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="listen-mode-dialog" className="max-w-lg max-h-[88vh] overflow-y-auto bg-[#0E131D] border-white/[0.08] text-slate-200 p-8 font-['Plus_Jakarta_Sans',sans-serif]">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold text-white flex items-center gap-2">
            <Ear size={22} className="text-[#38BDF8]" /> Listen mode
            {stars !== null && <span data-testid="listen-star-total" className="ml-auto text-sm font-mono text-[#FBBF24] flex items-center gap-1"><Star size={14} fill="currentColor" /> {stars}</span>}
          </DialogTitle>
          <DialogDescription className="text-slate-400">Press the button and talk. I'll listen and help you use the best words.</DialogDescription>
        </DialogHeader>
        <div className="grid place-items-center py-4">
          <MicButton r={rec} prefix="listen" />
          <p data-testid="listen-state" className="text-sm text-slate-400 mt-3">
            {rec.busy === "listening" ? "I'm listening… press stop when you're done" : rec.busy === "thinking" ? "Thinking…" : "Press to talk"}
          </p>
        </div>
        {result && <Result r={result} onStars={setStars} registerCancel={(fn) => { practiceCancel.current = fn; }} />}
      </DialogContent>
    </Dialog>
  );
};

export const ListenButton = ({ onClick, disabled }) => (
  <button type="button" data-testid="listen-mode-btn" onClick={onClick} disabled={disabled} className="what-surprise !text-[#38BDF8] !border-[#38BDF8]/60">
    <Volume2 size={15} /> Listen mode
  </button>
);
